import { showError } from '@components/app-error';
import { useRouter } from '@components/router';
import { showToast } from '@components/toaster';
import { useLocalStorageState } from 'client/lib/hooks/use-localstorage-state';
import { rpx } from 'client/lib/rpx-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { CategoryValue } from './category-filter';
import { promptMoveToFolder, promptNewFolder, promptRenameFolder } from './folder-dialogs';
import { showMediaPreview } from './preview-modal';
import { promptForRename } from './rename-dialog';
import { promptEditTags } from './tag-dialogs';
import {
  buildFiltersQuery,
  DEFAULT_FILTERS,
  emptyState,
  fetchPage,
  FolderFilter,
  ManagedFile,
  ManagedFolder,
  ManagedTag,
  MediaManagerMode,
  readUrlFilters,
  State,
  SortBy,
  View,
} from './state';

const SEARCH_DEBOUNCE_MS = 300;
const VIEW_STORAGE_KEY = 'mediaManagerView';
const RECENT_FOLDER_STORAGE_KEY = 'mediaManagerRecentFolder';

type FetchFilters = {
  category: CategoryValue;
  search: string;
  courseId: string | undefined;
  folderId: FolderFilter;
  tagId: string | undefined;
  sortBy: SortBy;
};

type UseMediaManagerOpts = {
  mode: MediaManagerMode;
  accept?: string;
  onPick?(files: ManagedFile[]): void;
  refreshSignal?: number;
  persistUrlState?: boolean;
  enableFolders?: boolean;
  onFolderChange?(folderId: FolderFilter): void;
};

export function useMediaManager({
  mode,
  accept,
  onPick,
  refreshSignal,
  persistUrlState,
  enableFolders,
  onFolderChange,
}: UseMediaManagerOpts) {
  const router = useRouter();
  const mimePrefixes = useMemo(() => parseAccept(accept), [accept]);
  const [storedView, setStoredView] = useLocalStorageState<View>(VIEW_STORAGE_KEY, 'grid');
  const [recentFolder, setRecentFolder] = useLocalStorageState<FolderFilter>(
    RECENT_FOLDER_STORAGE_KEY,
    undefined,
  );
  const [state, setState] = useState<State>(() => {
    const initialFolder: FolderFilter =
      enableFolders && !persistUrlState && recentFolder !== 'archived' ? recentFolder : undefined;
    if (!persistUrlState) {
      return emptyState({ folderId: initialFolder });
    }
    const f = readUrlFilters(location.search, {
      ...DEFAULT_FILTERS,
      view: storedView,
      folderId: undefined,
    });
    return emptyState({
      category: f.category,
      search: f.search,
      courseId: f.courseId,
      folderId: enableFolders ? f.folderId : undefined,
      tagId: f.tagId,
      sortBy: f.sortBy,
    });
  });
  const [view, setViewState] = useState<View>(() => {
    if (!persistUrlState) {
      return storedView;
    }
    return readUrlFilters(location.search, { ...DEFAULT_FILTERS, view: storedView }).view;
  });
  const setView = (v: View) => {
    setViewState(v);
    setStoredView(v);
  };
  const [folders, setFolders] = useState<ManagedFolder[]>([]);
  const [foldersLoaded, setFoldersLoaded] = useState(false);
  const [tags, setTags] = useState<ManagedTag[]>([]);
  const [tagSummaries, setTagSummaries] = useState<Array<ManagedTag & { fileCount: number }>>([]);
  const tagRefreshSequence = useRef(0);
  const [tagSummaryStatus, setTagSummaryStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    'idle',
  );
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const filtersRef = useRef<FetchFilters>(pickFilters(state));
  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const requestId = useRef(0);
  const isMounted = useRef(false);
  // Deep link: `/media-manager?file=<id>` opens that file's preview on load.
  // Captured during render (before the URL-sync effect below strips the param)
  // so a refresh or back-nav doesn't reopen it.
  const deepLinkFileId = useRef<string | undefined>(
    persistUrlState ? (new URLSearchParams(location.search).get('file') ?? undefined) : undefined,
  );

  function toggleSelect(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (s.size >= 100) {
          showToast({
            type: 'warn',
            title: '100 files selected',
            message: 'Clear the selection before choosing different files.',
          });
          return s;
        }
        next.add(id);
      }
      return next;
    });
  }

  function clearSelection() {
    setSelected((s) => (s.size === 0 ? s : new Set()));
  }

  function dropSelection(ids: Iterable<string>) {
    const removed = new Set(ids);
    setSelected((current) => {
      const next = new Set([...current].filter((id) => !removed.has(id)));
      return next.size === current.size ? current : next;
    });
  }

  const refreshFolders = useCallback(() => {
    return rpx.mediaManager
      .listFolders()
      .then((res) => {
        setFolders(res.folders);
        setFoldersLoaded(true);
      })
      .catch(showError);
  }, []);

  const refreshTags = useCallback(() => {
    const sequence = ++tagRefreshSequence.current;
    setTagSummaryStatus('loading');
    return rpx.mediaManager
      .listTagSummaries()
      .then((summaries) => {
        if (!isMounted.current || sequence !== tagRefreshSequence.current) {
          return;
        }
        setTags(summaries.map(({ id, name }) => ({ id, name })));
        setTagSummaries(summaries);
        setTagSummaryStatus('ready');
      })
      .catch((err) => {
        showError(err);
        if (isMounted.current && sequence === tagRefreshSequence.current) {
          setTagSummaryStatus('error');
        }
      });
  }, []);

  useEffect(() => {
    if (enableFolders) {
      refreshFolders();
      refreshTags();
    }
  }, [enableFolders, refreshFolders]);

  useEffect(() => {
    if (!persistUrlState) {
      return;
    }
    const url = buildFiltersQuery({
      category: state.category,
      search: state.search,
      courseId: state.courseId,
      folderId: enableFolders ? state.folderId : undefined,
      tagId: state.tagId,
      sortBy: state.sortBy,
      view,
    });
    if (url !== `${location.pathname}${location.search}`) {
      router.rewrite(url);
    }
  }, [
    persistUrlState,
    enableFolders,
    state.category,
    state.search,
    state.courseId,
    state.folderId,
    state.tagId,
    state.sortBy,
    view,
  ]);

  useEffect(() => {
    if (!enableFolders) {
      return;
    }
    if (!persistUrlState) {
      setRecentFolder(state.folderId);
    }
    onFolderChange?.(state.folderId);
  }, [enableFolders, state.folderId]);

  useEffect(() => {
    const folderId = state.folderId;
    if (
      persistUrlState ||
      !foldersLoaded ||
      !folderId ||
      folderId === 'root' ||
      folderId === 'archived' ||
      folders.some((folder) => folder.id === folderId)
    ) {
      return;
    }
    changeFolder(undefined);
  }, [foldersLoaded, folders, state.folderId]);

  // Open the deep-linked file's preview once on mount. Fetched on its own (it may
  // not be on the current page); a file the user doesn't own surfaces an error.
  useEffect(() => {
    const fileId = deepLinkFileId.current;
    if (!fileId) {
      return;
    }
    deepLinkFileId.current = undefined;
    rpx.mediaManager
      .getManagedFile({ fileId })
      .then(({ file }) => showMediaPreview(file))
      .catch(showError);
  }, []);

  function cancelRequests() {
    clearTimeout(debounce.current);
    return ++requestId.current;
  }

  async function runFetch(filters: FetchFilters, cursor?: string) {
    const id = cancelRequests();
    setState((s) => ({ ...s, isLoading: true, hasError: false }));
    try {
      const res = await fetchPage({ ...filters, cursor, mimePrefixes });
      if (id !== requestId.current) {
        return;
      }
      setState((s) =>
        filtersEqual(s, filters) && (!cursor || s.cursor === cursor)
          ? {
              ...s,
              items: cursor ? [...s.items, ...res.items] : res.items,
              cursor: res.cursor,
              hasMore: res.hasMore,
              isLoading: false,
              hasError: false,
            }
          : s,
      );
    } catch (err) {
      if (id === requestId.current) {
        showError(err);
        setState((s) =>
          filtersEqual(s, filters) ? { ...s, isLoading: false, hasError: true } : s,
        );
      }
    }
  }

  function loadMore() {
    if (!state.cursor || !state.hasMore || state.isLoading) {
      return;
    }
    void runFetch(filtersRef.current, state.cursor);
  }

  function reload({ keepItems = false } = {}) {
    if (!isMounted.current) {
      return;
    }
    clearSelection();
    setState((s) => ({
      ...s,
      items: keepItems ? s.items : [],
      cursor: undefined,
      hasMore: false,
    }));
    void runFetch(filtersRef.current);
  }

  function applyFilters(next: Partial<FetchFilters>, opts?: { debounce?: boolean }) {
    const filters = { ...filtersRef.current, ...next };
    if (filtersEqual(filtersRef.current, filters)) {
      return;
    }
    cancelRequests();
    clearSelection();
    filtersRef.current = filters;
    const searchOnly = Object.keys(next).length === 1 && next.search !== undefined;
    setState((s) => ({
      ...s,
      ...next,
      items: searchOnly ? s.items.filter((file) => matchesLocalSearch(file, filters.search)) : [],
      cursor: undefined,
      hasMore: false,
      isLoading: true,
      hasError: false,
    }));
    if (opts?.debounce && filters.search !== '') {
      debounce.current = setTimeout(() => void runFetch(filters), SEARCH_DEBOUNCE_MS);
      return;
    }
    void runFetch(filters);
  }

  useEffect(() => {
    isMounted.current = true;
    reload();
    return () => {
      isMounted.current = false;
      cancelRequests();
    };
  }, [mimePrefixes]);

  useEffect(() => {
    if (!refreshSignal) {
      return;
    }
    reload();
    if (enableFolders) {
      refreshFolders();
    }
  }, [refreshSignal]);

  function changeCategory(category: CategoryValue) {
    applyFilters({ category });
  }

  function changeCourse(courseId: string | undefined) {
    applyFilters({ courseId });
  }

  function changeFolder(folderId: FolderFilter) {
    applyFilters({ folderId, category: 'all', courseId: undefined, tagId: undefined, search: '' });
  }

  function clearFilters() {
    applyFilters({ category: 'all', courseId: undefined, tagId: undefined, search: '' });
  }

  function openFolder(folderId: FolderFilter) {
    applyFilters({
      folderId,
      category: 'all',
      courseId: undefined,
      tagId: undefined,
      search: '',
      sortBy: 'created',
    });
  }

  function changeTag(tagId: string | undefined) {
    applyFilters({ tagId });
  }

  function openTagFiles(tagId: string) {
    applyFilters({ folderId: undefined, category: 'all', courseId: undefined, tagId, search: '' });
  }

  function changeSort(sortBy: SortBy) {
    applyFilters({ sortBy });
  }

  function changeSearch(search: string) {
    applyFilters({ search }, { debounce: search !== '' });
  }

  async function createFolder() {
    const before = filtersRef.current;
    const name = await promptNewFolder();
    if (!name) {
      return;
    }
    try {
      const newFolder = await rpx.mediaManager.createFolder({ name });
      if (!isMounted.current) {
        return;
      }
      setFolders((fs) => [...fs, newFolder].sort((a, b) => a.name.localeCompare(b.name)));
      if (filtersRef.current === before) {
        changeFolder(newFolder.id);
      }
      showToast({ type: 'ok', title: 'Folder created', message: newFolder.name });
    } catch (err) {
      showError(err);
    }
  }

  async function renameFolder(folder: ManagedFolder) {
    const name = await promptRenameFolder(folder.name);
    if (!name || name === folder.name) {
      return;
    }
    try {
      await rpx.mediaManager.renameFolder({ folderId: folder.id, name });
      setFolders((fs) =>
        fs
          .map((f) => (f.id === folder.id ? { ...f, name } : f))
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
      showToast({ type: 'ok', title: 'Folder renamed', message: name });
    } catch (err) {
      showError(err);
    }
  }

  async function deleteFolder(folder: ManagedFolder) {
    try {
      await rpx.mediaManager.deleteFolder({ folderId: folder.id });
      if (!isMounted.current) {
        return;
      }
      setFolders((fs) => fs.filter((f) => f.id !== folder.id));
      refreshFolders();
      if (filtersRef.current.folderId === folder.id) {
        changeFolder('root');
      } else {
        reload();
      }
      showToast({ type: 'ok', title: 'Folder deleted', message: folder.name });
    } catch (err) {
      showError(err);
    }
  }

  async function createTag(name: string) {
    const tag = await rpx.mediaManager.createTag({ name });
    if (!isMounted.current) {
      return tag;
    }
    setTags((current) => [...current, tag].sort((a, b) => a.name.localeCompare(b.name)));
    void refreshTags();
    showToast({ type: 'ok', title: 'Tag created', message: tag.name });
    return tag;
  }

  async function renameTag(tag: ManagedTag, name: string) {
    if (name === tag.name) {
      return;
    }
    const renamed = await rpx.mediaManager.renameTag({ tagId: tag.id, name });
    if (!isMounted.current) {
      return;
    }
    setTags((current) =>
      current
        .map((item) => (item.id === tag.id ? renamed : item))
        .sort((a, b) => a.name.localeCompare(b.name)),
    );
    setState((s) => ({
      ...s,
      items: s.items.map((file) => ({
        ...file,
        tags: file.tags.map((item) => (item.id === tag.id ? renamed : item)),
      })),
    }));
    reload({ keepItems: true });
    void refreshTags();
    showToast({ type: 'ok', title: 'Tag renamed', message: renamed.name });
  }

  async function deleteTag(tag: ManagedTag) {
    await rpx.mediaManager.deleteTag({ tagId: tag.id });
    if (!isMounted.current) {
      return;
    }
    setTags((current) => current.filter((item) => item.id !== tag.id));
    if (filtersRef.current.tagId === tag.id) {
      changeTag(undefined);
    } else {
      setState((s) => ({
        ...s,
        items: s.items.map((file) => ({
          ...file,
          tags: file.tags.filter((item) => item.id !== tag.id),
        })),
      }));
      reload({ keepItems: true });
    }
    void refreshTags();
    showToast({ type: 'ok', title: 'Tag deleted', message: tag.name });
  }

  async function editTags(files: ManagedFile[]) {
    if (files.length === 0) {
      return;
    }
    const fileIds = files.map((file) => file.id);
    await promptEditTags(tags, files, async (changes) => {
      const { createdTags } = await rpx.mediaManager.updateFileTags({ fileIds, ...changes });
      if (!isMounted.current) {
        return;
      }
      const addIds = new Set(changes.addTagIds);
      const removeIds = new Set(changes.removeTagIds);
      const addTags = [...tags.filter((tag) => addIds.has(tag.id)), ...createdTags];
      setTags((current) =>
        [...current, ...createdTags].sort((a, b) => a.name.localeCompare(b.name)),
      );
      const fileIdSet = new Set(fileIds);
      setState((s) => {
        const items = s.items.map((file) => {
          if (!fileIdSet.has(file.id)) {
            return file;
          }
          const next = file.tags.filter((tag) => !removeIds.has(tag.id));
          const assigned = new Set(next.map((tag) => tag.id));
          for (const tag of addTags) {
            if (!assigned.has(tag.id)) {
              next.push(tag);
            }
          }
          return { ...file, tags: next.sort((a, b) => a.name.localeCompare(b.name)) };
        });
        return {
          ...s,
          items:
            s.tagId && removeIds.has(s.tagId)
              ? items.filter((file) => !fileIdSet.has(file.id))
              : items,
        };
      });
      reload({ keepItems: true });
      void refreshTags();
      clearSelection();
      showToast({
        type: 'ok',
        title: 'Tags updated',
        message: `${fileIds.length} file${fileIds.length === 1 ? '' : 's'}`,
      });
    });
  }

  function editFileTags(file: ManagedFile) {
    return editTags([file]);
  }

  function editSelectedTags() {
    const files = state.items.filter((file) => selected.has(file.id));
    return editTags(files);
  }

  async function moveFiles(ids: string[]) {
    if (ids.length === 0) {
      return;
    }
    const single = ids.length === 1 ? state.items.find((f) => f.id === ids[0]) : undefined;
    const target = await promptMoveToFolder({
      folders,
      currentFolderId: single ? (single.folderId ?? 'root') : undefined,
    });
    if (target === undefined) {
      return;
    }
    const folderId = target === 'root' ? undefined : target;
    try {
      await rpx.mediaManager.moveFiles({ fileIds: ids, folderId });
      if (!isMounted.current) {
        return;
      }
      const stillVisible = isMoveVisible(filtersRef.current.folderId, target);
      const idSet = new Set(ids);
      setState((s) => ({
        ...s,
        items: stillVisible
          ? s.items.map((f) => (idSet.has(f.id) ? { ...f, folderId } : f))
          : s.items.filter((f) => !idSet.has(f.id)),
      }));
      dropSelection(ids);
      reload({ keepItems: true });
      refreshFolders();
      showToast({
        type: 'ok',
        title: 'Moved',
        message: `${single ? single.name : `${ids.length} file${ids.length === 1 ? '' : 's'}`} → ${destFolderName(folders, target)}`,
      });
    } catch (err) {
      showError(err);
    }
  }

  async function setArchived(file: ManagedFile, archived: boolean) {
    const startedIn = filtersRef.current;
    const mutationRequest = cancelRequests();
    setState((s) => ({ ...s, items: s.items.filter((f) => f.id !== file.id) }));
    dropSelection([file.id]);
    try {
      if (archived) {
        await rpx.mediaManager.archiveFile({ fileId: file.id });
      } else {
        await rpx.mediaManager.unarchiveFile({ fileId: file.id });
      }
      if (!isMounted.current) {
        return;
      }
      if (enableFolders) {
        refreshFolders();
        refreshTags();
      }
      reload({ keepItems: true });
      showToast({ type: 'ok', title: archived ? 'Archived' : 'Unarchived', message: file.name });
    } catch (err) {
      showError(err);
      setState((s) =>
        requestId.current === mutationRequest &&
        filtersEqual(s, startedIn) &&
        !s.items.some((item) => item.id === file.id)
          ? { ...s, items: [file, ...s.items] }
          : s,
      );
      reload({ keepItems: true });
    }
  }

  async function rename(file: ManagedFile) {
    const name = await promptForRename(file.name);
    if (!name) {
      return;
    }
    const previousName = file.name;
    cancelRequests();
    setState((s) => ({
      ...s,
      items: s.items.map((f) => (f.id === file.id ? { ...f, name } : f)),
    }));
    try {
      await rpx.mediaManager.renameFile({ fileId: file.id, name });
      if (!isMounted.current) {
        return;
      }
      reload();
      showToast({ type: 'ok', title: 'Renamed', message: name });
    } catch (err) {
      showError(err);
      setState((s) => ({
        ...s,
        items: s.items.map((f) => (f.id === file.id ? { ...f, name: previousName } : f)),
      }));
      reload({ keepItems: true });
    }
  }

  function handleClick(file: ManagedFile) {
    if (mode === 'pick' && onPick) {
      // Picking an existing library file counts as a use — float it to the top
      // of the "recently used" ordering. Fire-and-forget; never block the pick.
      rpx.mediaManager.markFilesImported({ fileIds: [file.id] }).catch(() => undefined);
      onPick([file]);
      return;
    }
    void showMediaPreview(file);
  }

  const isManage = mode === 'manage';
  const isArchived = state.folderId === 'archived';
  const folderSelected = !!state.folderId && !isArchived;
  const hasOtherFilters =
    state.category !== 'all' || state.search.trim() !== '' || !!state.courseId || !!state.tagId;

  return {
    state,
    view,
    setView,
    folders,
    tags,
    refreshTags,
    tagSummaries,
    tagSummaryStatus,
    selected,
    isManage,
    isArchived,
    folderSelected,
    hasOtherFilters,
    loadMore,
    reload,
    toggleSelect,
    clearSelection,
    changeCategory,
    changeCourse,
    changeFolder,
    clearFilters,
    openFolder,
    changeTag,
    openTagFiles,
    changeSort,
    changeSearch,
    createFolder,
    renameFolder,
    deleteFolder,
    createTag,
    renameTag,
    deleteTag,
    moveFiles,
    editFileTags,
    editSelectedTags,
    setArchived,
    rename,
    handleClick,
  };
}

function pickFilters({ category, search, courseId, folderId, tagId, sortBy }: FetchFilters) {
  return { category, search, courseId, folderId, tagId, sortBy };
}

export function filtersEqual(a: FetchFilters, b: FetchFilters): boolean {
  return (
    a.category === b.category &&
    a.search === b.search &&
    a.courseId === b.courseId &&
    a.folderId === b.folderId &&
    a.tagId === b.tagId &&
    a.sortBy === b.sortBy
  );
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function matchesLocalSearch(file: ManagedFile, search: string): boolean {
  const term = search.trim().toLowerCase();
  if (!term) {
    return true;
  }
  return UUID_PATTERN.test(term)
    ? file.id.toLowerCase() === term
    : file.name.toLowerCase().includes(term);
}

function isMoveVisible(activeFolderId: FolderFilter, target: string): boolean {
  return activeFolderId === undefined || activeFolderId === 'archived' || activeFolderId === target;
}

function destFolderName(folders: ManagedFolder[], target: string): string {
  return target === 'root' ? 'Unfiled' : (folders.find((f) => f.id === target)?.name ?? 'folder');
}

export function parseAccept(accept?: string): string[] | undefined {
  if (!accept) {
    return undefined;
  }
  const parts = accept
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const prefixes = parts
    .filter((p) => p.includes('/'))
    .map((p) => (p.endsWith('/*') ? p.slice(0, -1) : p));
  return prefixes.length > 0 ? prefixes : undefined;
}
